import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { v4 as uuidv4 } from 'uuid';

import { Modal, useT } from '@repo/common';
import MobileActivityFullPage from './MobileActivityFullPage';

import { ReactComponent as DragAndDropIcon } from '@repo/common/assets/icons/16px/icon_16px_drag-and-drop.svg';
import { ReactComponent as DeleteIcon } from '@repo/common/assets/icons/24px/icon_24px_delete_outline.svg';
import { ReactComponent as PlusIcon } from '@repo/common/assets/icons/20px/icon_20px_plus_outline.svg';

const EMPTY_ENTRY = { title: '', date: '', description: '' };

const MobileActivityForm = ({ activity, setResumeData }) => {
  const t = useT();
  const placeholderText = t(
    'Tap to add your activity',
    '탭하여 활동을 추가해 주세요',
  );
  const list = Array.isArray(activity) ? activity : [];

  const [editor, setEditor] = useState({ open: false, mode: 'add', id: null });
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  const commit = (next) =>
    setResumeData((prev) => ({ ...prev, activity: next }));

  const openAdd = () => setEditor({ open: true, mode: 'add', id: null });
  const openEdit = (id) => setEditor({ open: true, mode: 'edit', id });
  const closeEditor = () => setEditor({ open: false, mode: 'add', id: null });

  const addRow = () => commit([...list, { id: uuidv4(), ...EMPTY_ENTRY }]);

  const handleSave = (data) => {
    if (editor.mode === 'add') {
      commit([...list, { id: uuidv4(), ...data }]);
    } else {
      commit(list.map((e) => (e.id === editor.id ? { ...e, ...data } : e)));
    }
    closeEditor();
  };

  const handleConfirmDelete = () => {
    commit(list.filter((e) => e.id !== deleteTargetId));
    setDeleteTargetId(null);
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(list);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    commit(items);
  };

  const editingEntry =
    editor.mode === 'edit' ? list.find((e) => e.id === editor.id) : null;

  return (
    <div className='flex flex-col gap-3 w-full'>
      {list.length === 0 ? (
        <div className='flex items-center w-full'>
          <span className='shrink-0 text-grayscale-gray04 py-[14px] w-[20px]'>
            <DragAndDropIcon />
          </span>
          <button
            type='button'
            onClick={openAdd}
            className='flex-1 min-w-0 text-left rounded-[8px] border border-grayscale-gray02 bg-grayscale-gray00 p-3 typo-b14Strong text-text-placeholder'
          >
            {placeholderText}
          </button>
        </div>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId='mobile-activities' direction='vertical'>
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className='flex flex-col gap-3 w-full'
              >
                {list.map((ac, index) => {
                  const title = ac.title?.trim();

                  return (
                    <Draggable
                      key={ac.id}
                      draggableId={String(ac.id)}
                      index={index}
                    >
                      {(prov) => (
                        <div
                          ref={prov.innerRef}
                          {...prov.draggableProps}
                          className='flex items-center gap-2 w-full'
                        >
                          <span
                            {...prov.dragHandleProps}
                            className='shrink-0 cursor-grab active:cursor-grabbing text-grayscale-gray03'
                          >
                            <DragAndDropIcon width={20} height={20} />
                          </span>

                          <div className='flex-1 min-w-0 flex items-center rounded-[8px] border border-grayscale-gray02 bg-grayscale-gray00'>
                            <button
                              type='button'
                              onClick={() => openEdit(ac.id)}
                              className='flex-1 min-w-0 flex items-start gap-1 text-left p-3'
                            >
                              <div className='flex flex-col min-w-0 w-full gap-1'>
                                <p
                                  className={`truncate typo-b14Strong ${
                                    title
                                      ? 'text-text-primary'
                                      : 'text-text-placeholder'
                                  }`}
                                >
                                  {title || placeholderText}
                                </p>
                                {ac.date?.trim() && (
                                  <p className='truncate typo-b12 text-text-secondary'>
                                    {ac.date}
                                  </p>
                                )}
                                {ac.description?.trim() && (
                                  <p className='line-clamp-2 break-words typo-b12 text-text-secondary'>
                                    {ac.description}
                                  </p>
                                )}
                              </div>

                              <div className='flex justify-end items-start w-6 max-h-[26px]'>
                                <button
                                  type='button'
                                  onClick={() => setDeleteTargetId(ac.id)}
                                  className='shrink-0 self-stretch flex items-start text-grayscale-gray04'
                                  aria-label={t('Delete', '삭제')}
                                >
                                  <DeleteIcon width={20} height={20} />
                                </button>
                              </div>
                            </button>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      <button
        type='button'
        onClick={addRow}
        className='flex items-center justify-center h-12 w-full rounded-[8px] bg-grayscale-gray01 text-grayscale-gray05 transition-colors active:bg-grayscale-gray02'
        aria-label={t('Add activity', '활동 추가')}
      >
        <PlusIcon width={20} height={20} />
      </button>

      {editor.open && (
        <MobileActivityFullPage
          initialData={editingEntry}
          onClose={closeEditor}
          onSave={handleSave}
        />
      )}

      {deleteTargetId && (
        <Modal
          isOpen
          onClickLeftBtn={() => setDeleteTargetId(null)}
          onClickRightBtn={handleConfirmDelete}
          title={t('Delete this activity entry?', '이 활동 항목을 삭제할까요?')}
          subText={t(
            'This action can’t be undone.',
            '이 작업은 취소할 수 없습니다.',
          )}
          buttonSize='xl'
          leftButtonLabel={t('cancel', '취소')}
          leftHideIcon={true}
          rightButtonLabel={t('Delete', '삭제')}
          rightHideIcon={true}
          type='warning'
        />
      )}
    </div>
  );
};

export default MobileActivityForm;
